import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Fade,
  Button,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  alpha,
} from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Dashboard as DashboardIcon,
  Home as HomeIcon,
  Logout as LogoutIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { Tournament, getTournamentLevels, getLevelRangeLabel } from "../types";
import { apiRequest } from "../config/api";
import { useAuth } from "../contexts/AuthContext";
import { useOrganization } from "../contexts/OrganizationContext";
import { useNavigate } from "react-router-dom";
import TournamentInvitationDialog from "../components/TournamentInvitationDialog";
import PartnerBoard from "../components/PartnerBoard";
import FormattedTextBlock from "../components/FormattedTextBlock";

interface PublicTournamentsProps {
  title?: string;
  embedded?: boolean;
  hideGoToDashboardButton?: boolean;
  /** Only show tournaments the logged-in player is already registered for. */
  onlyRegistered?: boolean;
}

export default function PublicTournaments({
  title = "Available Tournaments",
  embedded = false,
  hideGoToDashboardButton = false,
  onlyRegistered = false,
}: PublicTournamentsProps) {
  const { playerUser, playerToken, playerLogout } = useAuth();
  const { currentOrg } = useOrganization();
  const navigate = useNavigate();
  const isPadelEagle = currentOrg?.slug === "padel-eagle";
  const PE_BLUE = isPadelEagle ? "#743444" : "#1976d2";
  const PE_ORANGE = isPadelEagle ? "#E49421" : "#42a5f5";

  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTournament, setSelectedTournament] =
    useState<Tournament | null>(null);
  const [invitationDialogOpen, setInvitationDialogOpen] = useState(false);
  const [partnerBoardOpen, setPartnerBoardOpen] = useState(false);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
  const [receivedInvitations, setReceivedInvitations] = useState<any[]>([]);
  const [processingInviteId, setProcessingInviteId] = useState<string | null>(null);
  const [infoTournament, setInfoTournament] = useState<Tournament | null>(null);
  const [now, setNow] = useState(Date.now());
  const [registrationNotification, setRegistrationNotification] = useState<{ partnerName?: string; tournamentName?: string } | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const isRegisteredFor = (t: Tournament) => {
    const pid = playerUser?.playerId;
    if (!pid) return false;
    const activeStatuses = ["confirmed", "pending-approval", "waitlist"];
    return (
      registrations.some((reg: any) => reg.tournamentId === t.id && activeStatuses.includes(reg.status)) ||
      (Array.isArray(t.registeredTeams) &&
        t.registeredTeams.some((team: any) => team.player1Id === pid || team.player2Id === pid))
    );
  };
  const displayedTournaments = onlyRegistered ? tournaments.filter(isRegisteredFor) : tournaments;
  const browseLink = (
    <Typography variant="body2" sx={{ textAlign: "center", mt: 2 }}>
      To register and see available upcoming tournaments,{" "}
      <Box
        component="a"
        href="/view-tournaments"
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          navigate("/view-tournaments");
        }}
        sx={{ color: isPadelEagle ? "#743444" : PE_BLUE, fontWeight: "bold", cursor: "pointer" }}
      >
        click here
      </Box>
      .
    </Typography>
  );
  const autoExpandDoneRef = useRef(false);

  const toDate = (value: any): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === "string" || typeof value === "number") {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const seconds = value._seconds ?? value.seconds;
    if (typeof seconds === "number") return new Date(seconds * 1000);
    return null;
  };

  const formatCountdown = (targetDate: Date) => {
    const diff = Math.max(0, targetDate.getTime() - now);
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    return `${minutes}m ${seconds}s`;
  };

  useEffect(() => {
    const raw = sessionStorage.getItem("registrationSuccess");
    if (raw) {
      try { setRegistrationNotification(JSON.parse(raw)); } catch (_) {}
      sessionStorage.removeItem("registrationSuccess");
    }
  }, []);

  useEffect(() => {
    fetchTournaments();
    if (playerUser) {
      fetchRegistrationStatus();
    }
  }, [currentOrg, playerUser]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  // Auto-expand the embedded accordion once if there are upcoming tournaments,
  // otherwise leave it collapsed so an empty dashboard section stays compact.
  // Only applied once, right after the first load, so it doesn't fight manual toggling later.
  useEffect(() => {
    if (!embedded || !hasLoadedOnce || autoExpandDoneRef.current) return;
    autoExpandDoneRef.current = true;
    setExpanded(displayedTournaments.length > 0 || onlyRegistered);
  }, [embedded, hasLoadedOnce, tournaments, registrations]);

  const fetchRegistrationStatus = async () => {
    try {
      const [registrationsRes, sentRes, receivedRes] = await Promise.all([
        apiRequest("/api/tournament-registrations/my-registrations", {
          headers: { Authorization: `Bearer ${playerToken}` },
        }),
        apiRequest("/api/tournament-invitations/sent", {
          headers: { Authorization: `Bearer ${playerToken}` },
        }),
        apiRequest("/api/tournament-invitations/received", {
          headers: { Authorization: `Bearer ${playerToken}` },
        }),
      ]);

      if (registrationsRes.ok) {
        const regs = await registrationsRes.json();
        setRegistrations(Array.isArray(regs) ? regs : []);
      }

      if (sentRes.ok) {
        const invites = await sentRes.json();
        setPendingInvitations(
          Array.isArray(invites)
            ? invites.filter((inv: any) => inv.status === "pending")
            : [],
        );
      }

      if (receivedRes.ok) {
        const received = await receivedRes.json();
        setReceivedInvitations(
          Array.isArray(received)
            ? received.filter((inv: any) => inv.status === "pending")
            : [],
        );
      }
    } catch (error) {
      console.error("Error fetching registration status:", error);
    }
  };

  const handleAcceptInvitation = async (invitationId: string) => {
    setProcessingInviteId(invitationId);
    try {
      await apiRequest(`/api/tournament-invitations/${invitationId}/accept`, {
        method: "POST",
        headers: { Authorization: `Bearer ${playerToken}` },
      });
      await fetchRegistrationStatus();
    } catch (error) {
      console.error("Error accepting invitation:", error);
    } finally {
      setProcessingInviteId(null);
    }
  };

  const handleDeclineInvitation = async (invitationId: string) => {
    setProcessingInviteId(invitationId);
    try {
      await apiRequest(`/api/tournament-invitations/${invitationId}/decline`, {
        method: "POST",
        headers: { Authorization: `Bearer ${playerToken}` },
      });
      await fetchRegistrationStatus();
    } catch (error) {
      console.error("Error declining invitation:", error);
    } finally {
      setProcessingInviteId(null);
    }
  };

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      // Keep showing tournaments through the whole event (group-stage/knockout), not just
      // while registration is open — applies to logged-out visitors too, since generating a
      // draw moves a tournament out of "registration" and it would otherwise vanish from
      // /view-tournaments the moment the draw is created.
      params.append("status", "upcoming,registration,group-stage,knockout");

      if (playerUser?.playerId) {
        // Logged-in player: backend returns only tournaments they're allowed to see
        params.append("playerId", playerUser.playerId);
      } else {
        // Unauthenticated: backend hides restricted and hidden tournaments
        params.append("playerView", "true");
      }

      const response = await apiRequest(`/api/tournaments?${params}`);
      if (response.ok) {
        const tournamentsData = await response.json();
        setTournaments(Array.isArray(tournamentsData) ? tournamentsData : []);
      } else {
        setTournaments([]);
      }
    } catch (error) {
      console.error("Error fetching tournaments:", error);
      setTournaments([]);
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  };

  return (
    <Box sx={{ py: embedded ? 0 : 4 }}>
      <Fade in timeout={800}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, md: 3 },
            mb: { xs: 2, md: 3 },
            borderRadius: { xs: 2, md: 3 },
            border: isPadelEagle
              ? "1px solid rgba(116,52,68, 0.2)"
              : "1px solid rgba(25, 118, 210, 0.1)",
          }}
        >
          <Box
            onClick={embedded ? () => setExpanded((e) => !e) : undefined}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: !embedded || expanded ? 3 : 0,
              cursor: embedded ? "pointer" : "default",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "1.2rem", sm: "2.125rem" },
                color: isPadelEagle ? "#743444" : PE_BLUE,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <TrophyIcon fontSize="large" /> {title}
            </Typography>
            {embedded && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {hasLoadedOnce && (
                  <Chip
                    size="small"
                    label={
                      displayedTournaments.length > 0
                        ? `${displayedTournaments.length} upcoming`
                        : "None upcoming"
                    }
                    sx={{
                      fontWeight: "bold",
                      bgcolor: alpha(
                        displayedTournaments.length > 0
                          ? isPadelEagle
                            ? "#743444"
                            : PE_BLUE
                          : "#9e9e9e",
                        0.12
                      ),
                      color:
                        displayedTournaments.length > 0
                          ? isPadelEagle
                            ? "#743444"
                            : PE_BLUE
                          : "text.secondary",
                    }}
                  />
                )}
                <IconButton size="small" aria-label={expanded ? "Collapse" : "Expand"}>
                  <ExpandMoreIcon
                    sx={{
                      transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </IconButton>
              </Box>
            )}
          </Box>

          <Collapse in={!embedded || expanded} timeout="auto">
          {loading ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <CircularProgress />
              <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
                Loading tournaments...
              </Typography>
            </Box>
          ) : displayedTournaments.length > 0 ? (
            <>
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                {displayedTournaments.map((tournament) => {
                  const spotsRemaining =
                    tournament.maxTeams - tournament.registeredTeams.length;
                  const isFull = spotsRemaining <= 0;
                  const isRegistrationOpen =
                    tournament.status === "registration";
                  const maxTeams = Number(tournament.maxTeams) || 0;
                  const registeredTeamsCount = Array.isArray(tournament.registeredTeams)
                    ? tournament.registeredTeams.length
                    : 0;
                  const registrationRatio = maxTeams > 0 ? registeredTeamsCount / maxTeams : 0;
                  const showFewSpotsStamp = isRegistrationOpen && maxTeams > 0 && registrationRatio >= 0.5 && !isFull;
                  const isAdminManagedRegistration =
                    (tournament as any).playerRegistrationManagedByAdmin === true;
                  const hasDrawGenerated =
                    tournament.status === "group-stage" ||
                    tournament.status === "knockout" ||
                    tournament.status === "completed";
                  const startDate = new Date(tournament.startDate);
                  const registrationOpenDate = toDate((tournament as any).registrationOpenDate);
                  const isNotYetOpen = tournament.status === "upcoming";

                  // Check registration status for this tournament.
                  // Prefer active registration docs so old/inactive rows do not mask current state.
                  const tournamentRegistrations = registrations.filter(
                    (reg: any) => reg.tournamentId === tournament.id,
                  );
                  const activeRegistrationStatuses = new Set([
                    "confirmed",
                    "pending-approval",
                    "waitlist",
                  ]);
                  let myRegistration: any =
                    tournamentRegistrations.find((reg: any) =>
                      activeRegistrationStatuses.has(reg.status),
                    ) || null;
                  const registeredTeam = tournament.registeredTeams?.find(
                    (team: any) =>
                      team.player1Id === playerUser?.playerId ||
                      team.player2Id === playerUser?.playerId,
                  );
                  const myPendingInvitation = pendingInvitations.find(
                    (inv: any) => inv.tournamentId === tournament.id,
                  );
                  const myReceivedInvitation = receivedInvitations.find(
                    (inv: any) => inv.tournamentId === tournament.id,
                  );

                  let registrationStatus = "none";
                  let partnerName = "";
                  let invitationId = "";

                  // Fallback: if my-registrations did not include this team yet,
                  // infer confirmed registration from tournament.registeredTeams.
                  if (!myRegistration && registeredTeam) {
                    myRegistration = {
                      id: registeredTeam.id,
                      tournamentId: tournament.id,
                      status: "confirmed",
                      player1Id: registeredTeam.player1Id,
                      player2Id: registeredTeam.player2Id,
                      player1Name: registeredTeam.player1Name,
                      player2Name: registeredTeam.player2Name,
                    };
                  }

                  if (myRegistration) {
                    if (myRegistration.status === "confirmed") {
                      registrationStatus = "registered";
                      partnerName =
                        playerUser?.playerId === myRegistration.player1Id
                          ? myRegistration.player2Name
                          : myRegistration.player1Name;
                    } else if (myRegistration.status === "pending-approval") {
                      registrationStatus = "pending-approval";
                      partnerName =
                        playerUser?.playerId === myRegistration.player1Id
                          ? myRegistration.player2Name
                          : myRegistration.player1Name;
                    } else if (myRegistration.status === "waitlist") {
                      registrationStatus = "waitlisted";
                      partnerName =
                        playerUser?.playerId === myRegistration.player1Id
                          ? myRegistration.player2Name
                          : myRegistration.player1Name;
                    }
                  } else if (myPendingInvitation) {
                    if (myPendingInvitation.isNewPlayer) {
                      registrationStatus = "awaiting-registration";
                      partnerName =
                        myPendingInvitation.recipientName ||
                        myPendingInvitation.recipientEmail;
                      invitationId = myPendingInvitation.id;
                    } else {
                      registrationStatus = "awaiting-response";
                      partnerName =
                        myPendingInvitation.recipientName ||
                        myPendingInvitation.recipientEmail;
                      invitationId = myPendingInvitation.id;
                    }
                  }

                  const checklistEntry =
                    playerUser?.playerId && (tournament as any).playerChecklist
                      ? (tournament as any).playerChecklist[playerUser.playerId]
                      : null;
                  const partnerPlayerId =
                    registrationStatus === "registered" && myRegistration && playerUser?.playerId
                      ? (playerUser.playerId === myRegistration.player1Id
                          ? myRegistration.player2Id
                          : myRegistration.player1Id)
                      : null;
                  const partnerChecklistEntry =
                    partnerPlayerId && (tournament as any).playerChecklist
                      ? (tournament as any).playerChecklist[partnerPlayerId]
                      : null;
                  const selfPaymentConfirmed = !!checklistEntry?.paid;
                  const partnerPaymentConfirmed = !!partnerChecklistEntry?.paid;
                  const payInCash = !!(tournament as any).paymentSettings?.payInCash;
                  const paymentChipLabel =
                    registrationStatus === "waitlisted"
                      ? "Currently on the waitlist"
                      : registrationStatus === "registered"
                      ? payInCash
                        ? (selfPaymentConfirmed ? "Payment Confirmed" : null)
                        : `${selfPaymentConfirmed ? "Payment Confirmed" : "Pending Payment - Email with Payment Info will be sent soon"} · ${partnerPaymentConfirmed ? "Partner Payment Confirmed" : "Partner Payment Pending"}`
                      : null;
                  const drawEmailNote =
                    registrationStatus === "registered"
                      ? "Tournament draw will be sent to your email at least 1 week before the tournament."
                      : null;
                  const paymentIsPending =
                    !!paymentChipLabel &&
                    paymentChipLabel.startsWith("Pending Payment");
                  const paymentIsWaitlist =
                    !!paymentChipLabel &&
                    paymentChipLabel.startsWith("Currently on the waitlist");
                  const paymentDisplayLabel = paymentChipLabel
                    ? paymentIsPending
                      ? `⚠ ${paymentChipLabel}`
                      : paymentIsWaitlist
                        ? `⏳ ${paymentChipLabel}`
                      : `✅ ${paymentChipLabel}`
                    : null;
                  const teammatePlayerId =
                    myRegistration && playerUser?.playerId
                      ? (playerUser.playerId === myRegistration.player1Id
                          ? myRegistration.player2Id
                          : myRegistration.player1Id)
                      : null;
                  const teammateChecklistEntry =
                    teammatePlayerId && (tournament as any).playerChecklist
                      ? (tournament as any).playerChecklist[teammatePlayerId]
                      : null;
                  const withdrawalBlockedByPayment = !!(checklistEntry?.paid || teammateChecklistEntry?.paid);
                  const withdrawalBlockedMessage = "To cancel your registration please contact the tournament organizers directly on WhatsApp at +49 176 2019 3968.";

                  return (
                    <Grid item xs={12} sm={6} md={4} key={tournament.id}>
                      <Card
                        sx={{
                          position: "relative",
                          overflow: "hidden",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          border: "2px solid",
                          borderColor: isRegistrationOpen
                            ? "#E49421"
                            : "divider",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: { xs: "none", sm: "translateY(-4px)" },
                            boxShadow: 4,
                          },
                        }}
                      >
                        {showFewSpotsStamp && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 12,
                              right: -44,
                              zIndex: 2,
                              transform: "rotate(24deg)",
                              background: isPadelEagle ? "#E49421" : "linear-gradient(135deg, #c62828 0%, #ef6c00 100%)",
                              color: "#fff",
                              px: 5.5,
                              py: 0.65,
                              borderTop: "2px solid rgba(255,255,255,0.5)",
                              borderBottom: "2px solid rgba(255,255,255,0.5)",
                              boxShadow: isPadelEagle ? "none" : "0 8px 22px rgba(0,0,0,0.35)",
                              textAlign: "center",
                              animation: "fewSpotsPulse 1.4s ease-in-out infinite",
                              "@keyframes fewSpotsPulse": {
                                "0%": { filter: "brightness(1)", transform: "rotate(24deg) scale(1)" },
                                "50%": { filter: "brightness(1.1)", transform: "rotate(24deg) scale(1.03)" },
                                "100%": { filter: "brightness(1)", transform: "rotate(24deg) scale(1)" },
                              },
                            }}
                          >
                            <Typography variant="caption" sx={{ display: "block", fontWeight: 900, letterSpacing: "0.08em", lineHeight: 1.1 }}>
                              FEW SPOTS LEFT
                            </Typography>
                          </Box>
                        )}

                        {(tournament as any).posterImageUrl && (
                          <Box
                            component="img"
                            src={(tournament as any).posterImageUrl}
                            alt={`${tournament.name} poster`}
                            sx={{
                              width: "100%",
                              height: { xs: 110, sm: 180 },
                              objectFit: "cover",
                              borderBottom: "1px solid",
                              borderColor: "divider",
                            }}
                          />
                        )}

                        <CardContent sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{ fontWeight: "bold", fontSize: { xs: "1rem", sm: "1.25rem" } }}
                          >
                            {tournament.name}
                          </Typography>

                          <Divider sx={{ my: 1 }} />

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <EventIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {startDate.toLocaleDateString()}{tournament.startTime ? ` · ${tournament.startTime}` : ""}
                            </Typography>
                          </Box>

                          {tournament.venue && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              <span>📍</span>
                              <Typography variant="body2">
                                {tournament.venue}
                              </Typography>
                            </Box>
                          )}

                          <Box
                            sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                          >
                            {getTournamentLevels(tournament).map((lvl) => (
                              <Chip
                                key={lvl}
                                label={lvl}
                                color="primary"
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: "bold" }}
                              />
                            ))}
                            {isPadelEagle && getLevelRangeLabel(getTournamentLevels(tournament)) && (
                              <Chip
                                label={getLevelRangeLabel(getTournamentLevels(tournament))}
                                size="small"
                                sx={{ fontWeight: "bold", bgcolor: "#E49421", color: "#fff" }}
                              />
                            )}

                            {isRegistrationOpen ? (
                              <Chip
                                label="OPEN"
                                color="success"
                                size="small"
                                sx={{ fontWeight: "bold" }}
                              />
                            ) : (
                              <Chip
                                label={tournament.status.toUpperCase()}
                                color="default"
                                size="small"
                              />
                            )}

                            {paymentDisplayLabel && (
                              <>
                                <Chip
                                  label={paymentDisplayLabel}
                                  size="medium"
                                  variant="filled"
                                  sx={{
                                    mt: 0.5,
                                    width: "100%",
                                    height: "auto",
                                    borderRadius: 2,
                                    fontWeight: 800,
                                    letterSpacing: "0.01em",
                                    border: isPadelEagle
                                      ? paymentIsPending
                                        ? "2px solid #E49421"
                                        : paymentIsWaitlist
                                          ? "2px solid #74586C"
                                          : "2px solid #743444"
                                      : paymentIsPending
                                      ? "2px solid #ff8f00"
                                      : paymentIsWaitlist
                                        ? "2px solid #6a1b9a"
                                      : "2px solid #1b5e20",
                                    backgroundColor: isPadelEagle
                                      ? paymentIsPending
                                        ? "#F4C48B"
                                        : paymentIsWaitlist
                                          ? "#D8C4C1"
                                          : "#743444"
                                      : paymentIsPending
                                      ? "#ffca28"
                                      : paymentIsWaitlist
                                        ? "#ce93d8"
                                      : "#2e7d32",
                                    color: isPadelEagle
                                      ? paymentIsPending || paymentIsWaitlist
                                        ? "#743444"
                                        : "#ffffff"
                                      : paymentIsPending
                                      ? "#1f1f1f"
                                      : paymentIsWaitlist
                                        ? "#1f1f1f"
                                      : "#ffffff",
                                    boxShadow: isPadelEagle
                                      ? paymentIsPending
                                        ? "0 0 0 2px rgba(228, 148, 33, 0.32), 0 6px 16px rgba(228, 148, 33, 0.3)"
                                        : paymentIsWaitlist
                                          ? "0 0 0 2px rgba(116, 88, 108, 0.25)"
                                          : "0 0 0 2px rgba(116, 52, 68, 0.24)"
                                      : paymentIsPending
                                      ? "0 0 0 2px rgba(255, 179, 0, 0.32), 0 6px 16px rgba(255, 152, 0, 0.35)"
                                      : paymentIsWaitlist
                                        ? "0 0 0 2px rgba(106, 27, 154, 0.25), 0 6px 16px rgba(142, 36, 170, 0.25)"
                                      : "0 0 0 2px rgba(46, 125, 50, 0.24)",
                                    animation: paymentIsPending
                                      ? "paymentPulse 1.2s ease-in-out infinite"
                                      : "none",
                                    "@keyframes paymentPulse": {
                                      "0%": { transform: "scale(1)", boxShadow: "0 0 0 2px rgba(255, 179, 0, 0.32), 0 6px 16px rgba(255, 152, 0, 0.25)" },
                                      "50%": { transform: "scale(1.01)", boxShadow: "0 0 0 3px rgba(255, 179, 0, 0.45), 0 8px 22px rgba(255, 152, 0, 0.45)" },
                                      "100%": { transform: "scale(1)", boxShadow: "0 0 0 2px rgba(255, 179, 0, 0.32), 0 6px 16px rgba(255, 152, 0, 0.25)" },
                                    },
                                    "& .MuiChip-label": {
                                      whiteSpace: "normal",
                                      display: "block",
                                      py: 0.65,
                                    },
                                  }}
                                />
                                {drawEmailNote && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      mt: 0.75,
                                      display: "block",
                                      fontWeight: 600,
                                      color: "text.secondary",
                                    }}
                                  >
                                    {drawEmailNote}
                                  </Typography>
                                )}
                              </>
                            )}

                          </Box>

                          {tournament.description && (
                            <Box
                              component="button"
                              type="button"
                              onClick={() => setInfoTournament(tournament)}
                              sx={{
                                mt: 1.5,
                                p: 0,
                                border: 0,
                                background: "none",
                                cursor: "pointer",
                                font: "inherit",
                                fontSize: "0.875rem",
                                fontWeight: 600,
                                color: isPadelEagle ? "#743444" : PE_BLUE,
                                textDecoration: "underline",
                                textUnderlineOffset: 3,
                                "&:hover": { color: isPadelEagle ? "#E49421" : PE_ORANGE },
                              }}
                            >
                              ⓘ Show info
                            </Box>
                          )}
                        </CardContent>

                        {/* Sponsor logo strip */}
                        {Array.isArray((tournament as any).sponsors) && (tournament as any).sponsors.length > 0 && (
                          <Box
                            sx={{
                              mx: 2,
                              mb: 1,
                              pt: 1,
                              borderTop: "1px solid",
                              borderColor: "divider",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, whiteSpace: "nowrap" }}>
                              Sponsored by
                            </Typography>
                            {(tournament as any).sponsors.map((s: any, i: number) =>
                              s.websiteUrl ? (
                                <Box
                                  key={i}
                                  component="a"
                                  href={s.websiteUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  <Box
                                    component="img"
                                    src={s.logoUrl}
                                    alt={s.name}
                                    sx={{ height: 28, maxWidth: 80, objectFit: "contain", filter: "grayscale(0.2)" }}
                                  />
                                </Box>
                              ) : (
                                <Box
                                  key={i}
                                  component="img"
                                  src={s.logoUrl}
                                  alt={s.name}
                                  sx={{ height: 28, maxWidth: 80, objectFit: "contain", filter: "grayscale(0.2)" }}
                                />
                              )
                            )}
                          </Box>
                        )}

                        {/* Received invitation banner */}
                        {myReceivedInvitation && isRegistrationOpen && !myRegistration && (
                          <Box
                            sx={{
                              mx: 2,
                              mb: 1,
                              p: 1.5,
                              borderRadius: 1,
                              bgcolor: isPadelEagle
                                ? "rgba(116,52,68, 0.08)"
                                : "rgba(25, 118, 210, 0.08)",
                              border: "1px solid",
                              borderColor: isPadelEagle ? "#743444" : PE_BLUE,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, mb: 1 }}
                            >
                              🎾 <strong>{myReceivedInvitation.senderPlayerName}</strong> invited you to join their team!
                            </Typography>
                            {myReceivedInvitation.teamName && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ mb: 1 }}
                              >
                                Team: {myReceivedInvitation.teamName}
                              </Typography>
                            )}
                            {myReceivedInvitation.personalMessage && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                fontStyle="italic"
                                sx={{ mb: 1 }}
                              >
                                "{myReceivedInvitation.personalMessage}"
                              </Typography>
                            )}
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                fullWidth
                                disabled={processingInviteId !== null}
                                onClick={() =>
                                  handleDeclineInvitation(myReceivedInvitation.id)
                                }
                              >
                                {processingInviteId === myReceivedInvitation.id
                                  ? "..."
                                  : "Decline"}
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                color="success"
                                fullWidth
                                disabled={processingInviteId !== null}
                                onClick={() =>
                                  handleAcceptInvitation(myReceivedInvitation.id)
                                }
                              >
                                {processingInviteId === myReceivedInvitation.id
                                  ? "..."
                                  : "Accept"}
                              </Button>
                            </Box>
                          </Box>
                        )}

                        {playerUser && (registrationStatus === "registered" || registrationStatus === "waitlisted") && (
                          <Box sx={{ p: 2, pt: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                            <Button
                              variant="contained"
                              fullWidth
                              disabled
                              sx={{ bgcolor: isPadelEagle ? "#743444" : "success.main", "&.Mui-disabled": { color: "#fff", opacity: 0.85 } }}
                            >
                              ✅ Registered with {partnerName}
                            </Button>
                            {hasDrawGenerated && (
                              <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => navigate(`/tournament/${tournament.id}`)}
                              >
                                View Draw
                              </Button>
                            )}
                            {isRegistrationOpen && (
                              <Button
                                variant="text"
                                size="small"
                                color="error"
                                fullWidth
                                disabled={withdrawalBlockedByPayment}
                                onClick={async () => {
                                  if (withdrawalBlockedByPayment) return;
                                  if (
                                    window.confirm(
                                      `Are you sure you want to withdraw from ${tournament.name}?`,
                                    )
                                  ) {
                                    try {
                                      const withdrawResponse = await apiRequest(
                                        `/api/tournament-registrations/${myRegistration.id}/withdraw`,
                                        {
                                          method: "POST",
                                          headers: {
                                            Authorization: `Bearer ${playerToken}`,
                                          },
                                        },
                                      );
                                      if (!withdrawResponse.ok) {
                                        const err = await withdrawResponse.json().catch(() => ({}));
                                        throw new Error(err.error || "Failed to withdraw registration");
                                      }
                                      await fetchRegistrationStatus();
                                      await fetchTournaments();
                                    } catch (error: any) {
                                      console.error(
                                        "Error withdrawing registration:",
                                        error,
                                      );
                                      alert(error?.message || "Failed to withdraw registration");
                                    }
                                  }
                                }}
                              >
                                {withdrawalBlockedByPayment ? withdrawalBlockedMessage : "Withdraw Registration"}
                              </Button>
                            )}
                          </Box>
                        )}

                        {isRegistrationOpen && playerUser && !isAdminManagedRegistration && (
                          <Box
                            sx={{
                              p: 2,
                              pt: 0,
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            {registrationStatus === "none" && (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <Box sx={{ display: "flex", gap: 1, flexDirection: { xs: "column", sm: "row" } }}>
                                  <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={() => {
                                      setSelectedTournament(tournament);
                                      setInvitationDialogOpen(true);
                                    }}
                                    sx={{
                                      background: isPadelEagle
                                        ? "#E49421"
                                        : (isPadelEagle ? "#743444" : "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)"),
                                      "&:hover": {
                                        background: isPadelEagle
                                          ? "#74586C"
                                          : (isPadelEagle ? "#743444" : "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)"),
                                      },
                                    }}
                                  >
                                    Register
                                  </Button>
                                </Box>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  startIcon={<PersonIcon />}
                                  onClick={() => {
                                    setSelectedTournament(tournament);
                                    setPartnerBoardOpen(true);
                                  }}
                                >
                                  View Partner Board
                                </Button>
                              </Box>
                            )}

                            {registrationStatus === "awaiting-response" && (
                              <>
                                <Button
                                  variant="outlined"
                                  fullWidth
                                  disabled
                                  sx={{
                                    color: isPadelEagle ? "#E49421" : "warning.main",
                                    borderColor: isPadelEagle ? "#E49421" : "warning.main",
                                  }}
                                >
                                  ⏳ Awaiting {partnerName}'s Response
                                </Button>
                                <Button
                                  variant="text"
                                  size="small"
                                  color="error"
                                  fullWidth
                                  onClick={async () => {
                                    try {
                                      await apiRequest(
                                        `/api/tournament-invitations/${invitationId}/cancel`,
                                        {
                                          method: "POST",
                                          headers: {
                                            Authorization: `Bearer ${playerToken}`,
                                          },
                                        },
                                      );
                                      await fetchRegistrationStatus();
                                    } catch (error) {
                                      console.error(
                                        "Error canceling invitation:",
                                        error,
                                      );
                                    }
                                  }}
                                >
                                  Cancel Invitation
                                </Button>
                              </>
                            )}

                            {registrationStatus === "awaiting-registration" && (
                              <>
                                <Button
                                  variant="outlined"
                                  fullWidth
                                  disabled
                                  sx={{
                                    color: isPadelEagle ? "#74586C" : "info.main",
                                    borderColor: isPadelEagle ? "#74586C" : "info.main",
                                  }}
                                >
                                  ⏳ Awaiting {partnerName}'s Registration
                                </Button>
                                <Button
                                  variant="text"
                                  size="small"
                                  color="error"
                                  fullWidth
                                  onClick={async () => {
                                    try {
                                      await apiRequest(
                                        `/api/tournament-invitations/${invitationId}/cancel`,
                                        {
                                          method: "POST",
                                          headers: {
                                            Authorization: `Bearer ${playerToken}`,
                                          },
                                        },
                                      );
                                      await fetchRegistrationStatus();
                                    } catch (error) {
                                      console.error(
                                        "Error canceling invitation:",
                                        error,
                                      );
                                    }
                                  }}
                                >
                                  Cancel Invitation
                                </Button>
                              </>
                            )}

                            {registrationStatus === "pending-approval" && (
                              <Button
                                variant="outlined"
                                fullWidth
                                disabled
                                sx={{
                                  color: isPadelEagle ? "#74586C" : "info.main",
                                  borderColor: isPadelEagle ? "#74586C" : "info.main",
                                }}
                              >
                                📋 Pending Admin Approval with {partnerName}
                              </Button>
                            )}

                          </Box>
                        )}

                        {isRegistrationOpen && playerUser && isAdminManagedRegistration && registrationStatus === "none" && (
                          <Box sx={{ p: 2, pt: 0 }}>
                            <Button variant="outlined" fullWidth disabled>
                              Registration managed by admin
                            </Button>
                          </Box>
                        )}

                        {isNotYetOpen && (
                          <Box sx={{ p: 2, pt: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                            <Button variant="contained" fullWidth disabled>
                              Register (opens soon)
                            </Button>
                            <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                              {registrationOpenDate && registrationOpenDate.getTime() > now
                                ? `Opens in ${formatCountdown(registrationOpenDate)}`
                                : "Registration is not open yet"}
                            </Typography>
                          </Box>
                        )}

                        {isRegistrationOpen && !playerUser && (
                          <Box sx={{ p: 2, pt: 0 }}>
                            <Alert severity="info" sx={{ mb: 1, p: 1 }}>
                              <Typography variant="caption">
                                You need an account to register for tournaments
                              </Typography>
                            </Alert>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              <Box sx={{ display: "flex", gap: 1, flexDirection: { xs: "column", sm: "row" } }}>
                                <Button
                                  variant="contained"
                                  fullWidth
                                  onClick={() => {
                                    // Save current tournament for registration after account creation
                                    localStorage.setItem(
                                      "pendingTournamentRegistration",
                                      tournament.id,
                                    );
                                    window.location.href = "/player-registration";
                                  }}
                                  sx={{
                                    background: isPadelEagle
                                      ? "#E49421"
                                      : (isPadelEagle ? "#743444" : "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)"),
                                    "&:hover": {
                                      background: isPadelEagle
                                        ? "#74586C"
                                        : (isPadelEagle ? "#743444" : "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)"),
                                    },
                                  }}
                                >
                                  Create Account & Register
                                </Button>
                              </Box>
                              <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => {
                                  // Save the redirect path for after login
                                  localStorage.setItem(
                                    "redirectAfterLogin",
                                    window.location.pathname,
                                  );
                                  // Save current tournament for registration
                                  localStorage.setItem(
                                    "pendingTournamentRegistration",
                                    tournament.id,
                                  );
                                  window.location.href = "/player/login";
                                }}
                              >
                                Login to Register
                              </Button>
                            </Box>
                          </Box>
                        )}


                        {((!isRegistrationOpen && !isNotYetOpen) || isFull) && !playerUser && (
                          <Box sx={{ p: 2, pt: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                            <Alert severity="info" sx={{ mb: 0, p: 1 }}>
                              <Typography variant="caption">
                                Tournament registration may be full or closed, but you can still create your account now.
                              </Typography>
                            </Alert>
                            <Button
                              variant="outlined"
                              fullWidth
                              onClick={() => {
                                window.location.href = `/player-registration?tournamentId=${tournament.id}`;
                              }}
                            >
                              Create Tournament Account
                            </Button>
                          </Box>
                        )}
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              {onlyRegistered && browseLink}
            </>
          ) : onlyRegistered ? (
            <Box sx={{ textAlign: "center", py: { xs: 3, sm: 6 } }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                🎾 You haven't registered for any upcoming tournaments yet
              </Typography>
              {browseLink}
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                🎾 No tournaments available at the moment
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Check back soon for upcoming tournaments!
              </Typography>
              {playerUser && !hideGoToDashboardButton && (
                <Button
                  variant="outlined"
                  onClick={() => (window.location.href = "/player/dashboard")}
                  sx={{
                    borderColor: isPadelEagle ? "#743444" : PE_BLUE,
                    color: isPadelEagle ? "#743444" : PE_BLUE,
                    "&:hover": {
                      borderColor: isPadelEagle ? "#E49421" : PE_ORANGE,
                      backgroundColor: isPadelEagle
                        ? "rgba(116,52,68, 0.05)"
                        : "rgba(25, 118, 210, 0.05)",
                    },
                  }}
                >
                  Go to My Dashboard
                </Button>
              )}
            </Box>
          )}
          </Collapse>
        </Paper>
      </Fade>

      {/* Tournament Invitation Dialog */}
      {selectedTournament && playerUser && (
        <TournamentInvitationDialog
          open={invitationDialogOpen}
          onClose={() => {
            setInvitationDialogOpen(false);
            setSelectedTournament(null);
          }}
          tournament={selectedTournament}
          currentPlayer={
            {
              id: playerUser.playerId || "",
              name: playerUser.playerName || "",
              email: playerUser.username || "",
              level: playerUser.division ? 5 : undefined,
            } as any
          }
          onSuccess={() => {
            fetchTournaments();
            fetchRegistrationStatus();
          }}
        />
      )}

      <Dialog
        open={!!infoTournament}
        onClose={() => setInfoTournament(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{infoTournament?.name || "Tournament info"}</DialogTitle>
        <DialogContent dividers>
          {infoTournament?.description ? (
            <FormattedTextBlock text={infoTournament.description} color="text.primary" />
          ) : (
            <Typography variant="body1">No additional information available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoTournament(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Partner Board Dialog */}
      {selectedTournament && (
        <PartnerBoard
          open={partnerBoardOpen}
          onClose={() => {
            setPartnerBoardOpen(false);
            setSelectedTournament(null);
          }}
          tournament={selectedTournament}
          onInviteSent={() => {
            fetchTournaments();
            fetchRegistrationStatus();
          }}
        />
      )}

      {/* Post-registration success notification */}
      <Snackbar
        open={!!registrationNotification}
        onClose={() => setRegistrationNotification(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={10000}
      >
        <Alert
          onClose={() => setRegistrationNotification(null)}
          severity="success"
          sx={{ width: "100%", fontSize: "1rem" }}
        >
          {registrationNotification?.partnerName && registrationNotification?.tournamentName
            ? `🎾 You've been successfully registered with ${registrationNotification.partnerName} in ${registrationNotification.tournamentName}!`
            : registrationNotification?.partnerName
            ? `🎾 You've been successfully registered with ${registrationNotification.partnerName}!`
            : "🎾 Your registration has been confirmed!"}
        </Alert>
      </Snackbar>
    </Box>
  );
}
